import React, {
    Component,
    createRef,
    SyntheticEvent,
    KeyboardEvent,
    RefObject,
} from 'react';
import cx from 'classnames';

import { hasAcceptedFileType, getFileInputAccept } from '@trqk/utils';

type Props = {
    acceptMultiple?: boolean;
    acceptFileTypes?: FileTypeKey[];
    addClasses?: string;
    children?: string | JSX.Element | JSX.Element[];
    handleFiles(fileInputRef: RefObject<HTMLInputElement>): void;
    handleSelectedFilesWithWrongType?(filesWithWrongType: File[]): void;
    inputId: string;
};

// An <input> element that is hidden, allowing for any other element to be used as a trigger for
// adding files.
// If you provide an `acceptFileTypes` array, you probably also should provide a
// `handleSelectedFilesWithWrongType` callback to give the user some feedback on why the files
// were not accepted.
export class FileInput extends Component<Props> {
    private inputElRef = createRef<HTMLInputElement>();

    constructor(props: Props) {
        super(props);
        this.handleChange = this.handleChange.bind(this);
        this.handleClick = this.handleClick.bind(this);
    }

    handleChange(e: SyntheticEvent): void {
        const {
            acceptFileTypes,
            handleFiles,
            handleSelectedFilesWithWrongType,
        } = this.props;

        e.preventDefault();

        if (this.inputElRef.current) {
            if (Array.isArray(acceptFileTypes)) {
                const files = Array.from(this.inputElRef?.current?.files ?? []);
                const filesWithWrongType = files.filter(
                    (file: File): boolean =>
                        !hasAcceptedFileType(file, acceptFileTypes),
                );

                if (filesWithWrongType.length > 0) {
                    // If we detect a wrong file type, we don't handle the files - instead, we call
                    // `handleSelectedFilesWithWrongType` if it was provided
                    if (handleSelectedFilesWithWrongType) {
                        handleSelectedFilesWithWrongType(filesWithWrongType);
                    }
                } else {
                    handleFiles(this.inputElRef);
                }
            } else {
                // If `acceptFileTypes` is not provided, we handle all files that are selected.
                handleFiles(this.inputElRef);
            }
        }
    }

    handleClick(): void {
        if (this.inputElRef.current) {
            this.inputElRef.current.click();
        }
    }

    render(): JSX.Element {
        const {
            acceptFileTypes,
            acceptMultiple = false,
            addClasses,
            children,
            inputId,
        } = this.props;
        // This will filter what files the user is allowed to select with the browser dialog, but
        // only on a non-Windows OS. In Windows, any file type will be selectable.
        const accept = getFileInputAccept(acceptFileTypes);

        return (
            <div
                role="button"
                className={cx(addClasses)}
                onClick={this.handleClick}
                onKeyPress={(e: KeyboardEvent): void => {
                    if (e.key === 'Enter') {
                        this.handleClick();
                    }
                }}
                tabIndex={0}
            >
                {children}
                <input
                    className="hidden pointer-events-none"
                    id={inputId}
                    multiple={acceptMultiple}
                    onChange={this.handleChange}
                    ref={this.inputElRef}
                    type="file"
                    accept={accept}
                    data-cy="file-input"
                />
            </div>
        );
    }
}
